import React, { useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { useUtils } from "../../../context/UtilsContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { bmiCategory } from "../../../utils/bodyMetrics";
import {
  peekProfileDigest,
  prefetchUserProfile,
  resolveProfileEventTypeId,
} from "../../../utils/userProfileDigest";
import { useClientInsights } from "../../../hooks/insights/useClientInsights";
import { TRACKING_WINDOW_DAYS } from "../../../hooks/insights/insightDerive";

/** Tags shown before the "+N" overflow toggle, per group. */
const TAGS_BEFORE_OVERFLOW = 3;

/** Initials for the avatar circle, at most two letters. */
function initialsOf(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
}

/** Semantic BMI tones → palette. Red for obese is the point of the band. */
const BMI_TONES = {
  low: "bg-[#E6F1FB] text-[#2A6EB0]",
  normal: "bg-[#EAF3DE] text-[#1D9E75]",
  warn: "bg-[#FAEEDA] text-[#633806]",
  high: "bg-[#FCEBEB] text-[#A32D2D]",
};

/**
 * One value inside a group. `sub` carries the secondary number that belongs
 * with it (ideal weight under weight, BMR under TDEE) instead of competing for
 * a cell of its own.
 */
function Value({ label, value, unit, sub }) {
  return (
    <div className="min-w-0">
      <div className="whitespace-nowrap text-[13px] font-semibold leading-none text-gray-900">
        {value == null ? "—" : value}
        {value != null && unit ? (
          <span className="ml-0.5 text-[10px] font-normal text-gray-500">
            {unit}
          </span>
        ) : null}
      </div>
      <div className="mt-1 whitespace-nowrap text-[9px] font-medium uppercase leading-none tracking-wide text-gray-400">
        {sub || label}
      </div>
    </div>
  );
}

/**
 * A titled cluster of related values, separated from its neighbours.
 *
 * Eight equal cells in a row meant reading every label to find one number.
 * Grouping into body / BMI / energy gives each value a location, so the eye
 * goes to the right cluster first and only then to the value.
 */
function MetricGroup({ title, children }) {
  return (
    <div className="flex flex-col gap-1 px-2.5">
      <span className="text-[8px] font-bold uppercase leading-none tracking-widest text-gray-300">
        {title}
      </span>
      <div className="flex items-start gap-3">{children}</div>
    </div>
  );
}

/**
 * A labelled row of small chips that collapses past a few entries.
 *
 * Clients routinely carry seven fitness goals and three conditions; rendering
 * them all inline would push the tab body off screen, and truncating silently
 * would hide clinically relevant history. So: first few plus a "+N" that
 * expands in place.
 */
function TagGroup({ label, items, tone = "neutral" }) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;

  const hidden = items.length - TAGS_BEFORE_OVERFLOW;
  const shown = expanded ? items : items.slice(0, TAGS_BEFORE_OVERFLOW);
  const tones = {
    neutral: "bg-gray-100 text-gray-600",
    warn: "bg-[#FCEBEB] text-[#A32D2D]",
  };

  return (
    <div className="flex min-w-0 items-baseline gap-1.5">
      <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {shown.map((item) => (
          <span
            key={item}
            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] leading-4 ${tones[tone]}`}
          >
            {item}
          </span>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded px-1 text-[10px] font-semibold leading-4 text-orange-primary hover:underline"
          >
            {expanded ? "less" : `+${hidden}`}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Identity and vitals band for the Users split panel.
 *
 * Deliberately not `ProfileCard`: that card is a 400px-tall editing surface
 * built for the full `/dashboard/:id` page, which leaves no room for a tab body
 * beside a list. This carries the same facts a coach actually reads — sex, age,
 * height, weight, BMI, ideal weight, BMR, TDEE, plus diet, goals, conditions
 * and injuries — in a fraction of the height, and stays pinned while the tab
 * body scrolls so it's still there deep inside the diet plan tab.
 * `/dashboard/:id` keeps the full editable card, untouched.
 *
 * It also guarantees the profile is fetched-and-cached: hover prefetch usually
 * wins the race, and this is the backstop for a deep link or a fast click.
 *
 * @param {Object} props
 * @param {string} props.userId
 * @param {string} [props.fallbackName] - Row name, until the profile resolves.
 * @param {React.ReactNode} [props.actions] - Trailing controls (expand, close).
 */
/**
 * Plan age in days, or the word that says why there is no number. The roster
 * shows these as icons with the detail in a tooltip; this is the one surface
 * with room to just print them.
 */
function planAgeValue({ never, unknown, days }) {
  if (unknown) return null;
  if (never) return "Never";
  return days;
}

export function UserProfileStrip({ userId, fallbackName, actions }) {
  const { sessionUtils } = useUtils();
  const [digest, setDigest] = useState(() => peekProfileDigest(userId));
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const { getTags, hasInsights } = useClientInsights();
  const tags = getTags(userId);

  // On a phone the full band wraps into four stacked rows and eats roughly half
  // the screen before the tab body gets a pixel, so the metrics and tags sit
  // behind a tap. From `sm` up nothing is hidden and there is no toggle.
  const showDetails = !isMobile || expanded;

  // A different client in the same drawer starts collapsed again.
  useEffect(() => setExpanded(false), [userId]);

  useEffect(() => {
    let active = true;
    // Paint whatever is cached for this user immediately, then ensure a fetch.
    setDigest(peekProfileDigest(userId));

    const eventTypeId = resolveProfileEventTypeId(sessionUtils);
    if (!eventTypeId || !userId) return undefined;

    prefetchUserProfile(eventTypeId, userId).then((ok) => {
      if (active && ok) setDigest(peekProfileDigest(userId));
    });

    return () => {
      active = false;
    };
  }, [userId, sessionUtils]);

  const name = digest?.name || fallbackName;
  // Sex, age and contact never change — they're identity, not metrics, so they
  // sit with the name instead of taking equal-weight cells in the numbers block.
  const identity = [
    digest?.gender,
    digest?.age != null ? `${digest.age}y` : null,
    digest?.phone,
    digest?.email,
  ].filter(Boolean);
  const bmiBand = bmiCategory(digest?.bmi);
  // The gap to ideal weight is the comparison a coach actually makes; showing
  // ideal as its own number leaves them doing the subtraction.
  const idealDelta =
    digest?.weightKg != null && digest?.idealWeightKg != null
      ? Math.round((digest.weightKg - digest.idealWeightKg) * 10) / 10
      : null;
  const hasTags =
    digest &&
    (digest.dietaryPreferences.length ||
      digest.fitnessGoals.length ||
      digest.medicalHistories.length ||
      digest.injuries.length);

  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* Left: who */}
        <div className="flex min-w-[11rem] flex-1 items-center gap-2.5">
          <span
            role="img"
            aria-label={name || "User"}
            className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-light-orange text-xs font-semibold text-[#C2410C] ring-2 ring-[#FFD9C2]"
          >
            {initialsOf(name)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight text-gray-900">
              {name || "Loading…"}
            </p>
            {identity.length > 0 && (
              <p className="truncate text-[11px] leading-tight text-gray-500">
                {identity.join(" · ")}
              </p>
            )}
          </div>
        </div>

        {/* Right: three separated clusters, always in the same order, so a
            value has a place rather than being one of eight lookalikes.
            Narrow screens scroll the clusters instead of clipping TDEE off
            the edge. */}
        <div
          className={`min-w-0 max-w-full flex-shrink-0 items-stretch divide-x divide-gray-200 overflow-x-auto rounded-lg border border-gray-200 py-1.5 ${
            showDetails ? "flex" : "hidden"
          }`}
        >
          <MetricGroup title="Body">
            <Value label="Height" value={digest?.heightCm} unit="cm" />
            <Value
              label="Weight"
              value={digest?.weightKg}
              unit="kg"
              sub={
                idealDelta == null
                  ? "Weight"
                  : `${idealDelta > 0 ? "+" : ""}${idealDelta} vs ideal`
              }
            />
          </MetricGroup>

          <MetricGroup title="BMI">
            <div className="min-w-0">
              <div className="whitespace-nowrap text-[13px] font-semibold leading-none text-gray-900">
                {digest?.bmi == null ? "—" : digest.bmi}
              </div>
              {bmiBand ? (
                <span
                  className={`mt-1 inline-block whitespace-nowrap rounded px-1 py-px text-[9px] font-semibold uppercase leading-none tracking-wide ${
                    BMI_TONES[bmiBand.tone]
                  }`}
                >
                  {bmiBand.label}
                </span>
              ) : (
                <div className="mt-1 text-[9px] font-medium uppercase leading-none tracking-wide text-gray-400">
                  BMI
                </div>
              )}
            </div>
          </MetricGroup>

          <MetricGroup title="Energy · kcal/day">
            <Value label="BMR" value={digest?.bmr} />
            <Value label="TDEE" value={digest?.tdee} />
          </MetricGroup>

          {hasInsights && (
            <MetricGroup title="Coaching">
              <Value
                label="Diet plan"
                value={planAgeValue({
                  never: tags.noDietPlan,
                  unknown: tags.dietUnknown,
                  days: tags.dietAgeDays,
                })}
                unit={tags.noDietPlan ? "" : "d ago"}
                sub="Diet plan"
              />
              <Value
                label="Workout plan"
                value={planAgeValue({
                  never: tags.noWorkoutPlan,
                  unknown: tags.workoutUnknown,
                  days: tags.workoutAgeDays,
                })}
                unit={tags.noWorkoutPlan ? "" : "d ago"}
                sub="Workout plan"
              />
              {/* Days with at least one log, not entry counts — so the value
                  reads against the window rather than being unbounded. */}
              <Value
                label="Food days"
                value={tags.hasData ? tags.foodLogs : null}
                unit={`/${TRACKING_WINDOW_DAYS}d`}
                sub="Food days"
              />
              <Value
                label="Workout days"
                value={tags.hasData ? tags.workoutLogs : null}
                unit={`/${TRACKING_WINDOW_DAYS}d`}
                sub="Workout days"
              />
              <Value
                label="With you"
                value={tags.attachedDaysAgo}
                unit="d"
                sub="With you"
              />
            </MetricGroup>
          )}
        </div>

        {(isMobile || actions) && (
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {isMobile && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-label={
                  expanded ? "Hide profile details" : "Show profile details"
                }
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <FiChevronDown
                  aria-hidden="true"
                  className={`h-4 w-4 transition-transform ${
                    expanded ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
            {actions}
          </div>
        )}
      </div>

      {/* Second line: qualitative context. Omitted entirely when the profile
          carries none, rather than showing four empty labels. */}
      {hasTags && showDetails ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
          <TagGroup label="Diet" items={digest.dietaryPreferences} />
          <TagGroup label="Goals" items={digest.fitnessGoals} />
          <TagGroup label="History" items={digest.medicalHistories} tone="warn" />
          <TagGroup label="Injury" items={digest.injuries} tone="warn" />
        </div>
      ) : null}
    </div>
  );
}

export default UserProfileStrip;
